import { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Page, Navbar, Link, Badge } from 'konsta/react'
import { Home, Grid3X3, Wallet, User, Bell } from 'lucide-react'
import { useNotificationStore } from '@/stores/notificationStore'
import { useAnnouncementStore, startAnnouncementDemo } from '@/stores/announcementStore'
import { NotificationSheet } from './NotificationSheet'

const tabs = [
  { path: '/', icon: Home, label: '首頁' },
  { path: '/rooms', icon: Grid3X3, label: '房間' },
  { path: '/wallet', icon: Wallet, label: '錢包' },
  { path: '/profile', icon: User, label: '我的' },
]

export function KonstaLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [notifOpen, setNotifOpen] = useState(false)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const announcement = useAnnouncementStore((s) => s.current)
  const dismissAnnouncement = useAnnouncementStore((s) => s.dismiss)

  useEffect(() => {
    startAnnouncementDemo()
  }, [])

  // Auto-dismiss announcement after 6 seconds
  useEffect(() => {
    if (!announcement) return
    const timer = setTimeout(dismissAnnouncement, 6000)
    return () => clearTimeout(timer)
  }, [announcement, dismissAnnouncement])

  return (
    <Page className="pb-[88px]">
      <Navbar
        title="PWG Lounge"
        right={
          <Link onClick={() => setNotifOpen(true)} className="relative">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <Badge colors={{ bg: 'bg-red-500' }} className="absolute -top-1 -right-1">
                {unreadCount}
              </Badge>
            )}
          </Link>
        }
      />

      <Outlet />

      {/* Floating announcement — fixed, overlays content, no space consumed */}
      {announcement && (
        <div className="fixed top-[calc(max(16px,var(--k-safe-area-top))+48px)] left-3 right-3 z-30">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl px-4 py-2.5 flex items-center justify-between shadow-lg border border-black/5">
            <p className="text-[12px] text-[#007AFF] font-semibold flex-1 truncate">
              {announcement.message}
            </p>
            <button
              onClick={dismissAnnouncement}
              className="ml-3 w-6 h-6 rounded-full bg-black/5 flex items-center justify-center flex-shrink-0"
            >
              <span className="text-[#8E8E93] text-[11px] font-bold">✕</span>
            </button>
          </div>
        </div>
      )}

      {/* Custom frosted glass tab bar — z-20 so Konsta Sheets/Actions (z-40) overlay above */}
      <div className="fixed bottom-0 left-0 right-0 z-20">
        {/* Hairline top border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-black/[0.12]" />
        {/* Frosted glass background */}
        <div className="bg-white/70 backdrop-blur-xl backdrop-saturate-[1.8]">
          <div className="flex items-stretch pb-[env(safe-area-inset-bottom)]">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = location.pathname === tab.path
              return (
                <button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  className="flex-1 flex flex-col items-center gap-0.5 pt-2 pb-1.5 transition-colors"
                >
                  <div className="relative">
                    <Icon
                      className="w-[22px] h-[22px] transition-colors"
                      strokeWidth={isActive ? 2.2 : 1.5}
                      style={{ color: isActive ? '#007AFF' : '#999' }}
                    />
                    {isActive && (
                      <div className="absolute -inset-1.5 rounded-full bg-[#007AFF]/8 -z-10" />
                    )}
                  </div>
                  <span
                    className="text-[10px] font-medium transition-colors"
                    style={{ color: isActive ? '#007AFF' : '#999' }}
                  >
                    {tab.label}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <NotificationSheet open={notifOpen} onOpenChange={setNotifOpen} />
    </Page>
  )
}
